import { useMemo } from 'react';
import { useWorkPeriodsMeasureMultiple } from '../../../api/work-periods-client/work-periods-client.hooks';
import {
  EntityType,
  Measure,
  ModuleType,
  Transformer,
  type MeasureDataByEntity,
  type MeasureDataResponse,
} from '../../../api/work-periods-client/work-periods-client.type';
import { formatToISODate } from '../../../helpers/date-time-helpers';
import { useGlobalStore } from '../../../store/global-store/global-store';
import { useProcessAnalysisStore } from '../../../store/process-analysis-store/process-analysis-store';
import { useDateRange, useTimeAllocation } from '../../../store/process-analysis-store/process-analysis-store.hooks';
import { Tab } from '../process-analysis.type';

/**
 * Custom hook that fetches capacity data for selected teams, following the V1 workload distribution pattern
 *
 * This hook maintains the same data fetching logic as the V1 workload distribution feature:
 * - Fetches both portfolio-level totals and individual team data
 * - Uses workload_distribution_capacity measure with cumulative_singular_value transformer
 * - Follows the same payload structure and API integration pattern
 *
 * @param {boolean} [enabled=true] - Whether the hook should fetch data
 * @returns {Object} Object containing portfolio data, project data, and loading state
 */
const useCapacityData = (
  enabled = true,
): {
  portfolioData: MeasureDataResponse | undefined;
  projectData: MeasureDataByEntity | undefined;
  isFetching: boolean;
  isLoading: boolean;
} => {
  const portfolio = useGlobalStore((state) => state.portfolio);
  const { startDate, endDate } = useDateRange();
  const timeAllocation = useTimeAllocation();
  const selectedTaskType = useProcessAnalysisStore((state) => state.selectedTaskType);

  const teamIds = useTeamIds();

  const hasValidPortfolio = Boolean(portfolio?.id);
  const hasValidTeams = teamIds.length > 0;

  const taskTypePayload = selectedTaskType ? { task_type: selectedTaskType } : {};

  const portfolioPayload = {
    entity: EntityType.Portfolio,
    ids: portfolio?.id ? [portfolio.id] : [],
    start_date: formatToISODate(startDate),
    end_date: formatToISODate(endDate),
    measure_name: [Measure.WorkloadDistributionCapacity],
    time_allocation_type: timeAllocation,
    is_custom: true,
    transformer: Transformer.CumulativeSingularValue,
    module_type: ModuleType.ProcessAnalysis,
    ...taskTypePayload,
  };

  const projectPayload = {
    entity: EntityType.Project,
    ids: teamIds,
    start_date: formatToISODate(startDate),
    end_date: formatToISODate(endDate),
    measure_name: [Measure.WorkloadDistributionCapacity],
    time_allocation_type: timeAllocation,
    is_custom: true,
    transformer: Transformer.CumulativeSingularValue,
    module_type: ModuleType.ProcessAnalysis,
    ...taskTypePayload,
  };

  const {
    data: portfolioData,
    isFetching: isFetchingPortfolioData,
    isLoading: isLoadingPortfolioData,
  } = useWorkPeriodsMeasureMultiple({
    payload: portfolioPayload,
    options: { enabled: enabled && hasValidPortfolio },
  });

  const {
    data: projectData,
    isFetching: isFetchingProjectData,
    isLoading: isLoadingProjectData,
  } = useWorkPeriodsMeasureMultiple({
    payload: projectPayload,
    options: { enabled: enabled && hasValidTeams && hasValidPortfolio },
  });

  return {
    portfolioData: portfolio?.id ? portfolioData?.[portfolio.id] : undefined,
    projectData,
    isFetching: isFetchingPortfolioData || isFetchingProjectData,
    isLoading: isLoadingPortfolioData || isLoadingProjectData,
  };
};

/**
 * Hook that returns an array of team IDs based on the active tab and selected teams
 *
 * When the active tab is Teams, returns only the selected team IDs
 * Otherwise returns IDs of all teams in the portfolio
 *
 * @returns {string[]} Array of team IDs
 */
const useTeamIds = (): string[] => {
  const portfolio = useGlobalStore((state) => state.portfolio);
  const teams = portfolio?.projects || [];
  const selectedTeams = useProcessAnalysisStore((state) => state.selectedTeams);
  const activeTab = useProcessAnalysisStore((state) => state.activeTab);

  const teamIds = useMemo(() => {
    if (activeTab === Tab.Teams) {
      return selectedTeams;
    }

    return teams.map((team) => team.id);
  }, [activeTab, selectedTeams, teams]);

  return teamIds;
};

export { useCapacityData };
